import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

export interface PromisesWidgetSummaryApi {
  promisesDueToday: {
    count: number;
  };
  brokenPromises: {
    count: number;
    totalAmount: number;
  };
}

/**
 * Hook to fetch promises widget summary for the dashboard.
 * @param assignedUserId — when provided (live mode only), passes `?assignedUserId=<id>` so the
 *   backend returns counts scoped to that user. Pass `undefined` for the "Everyone" view.
 */
export function usePromisesWidgetSummary(assignedUserId?: string) {
  const { apiGet } = useApi();

  const store = getGlobalDataStore();
  const allPromises = store.getAllPromises();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const promisesDueToday = allPromises.filter((x) => {
    const promiseDate = new Date(x.promiseDate);
    promiseDate.setHours(0, 0, 0, 0);
    return promiseDate.getTime() === today.getTime() && x.status === 'Active';
  });

  const brokenPromises = allPromises.filter((x) => x.status === 'Broken');

  const demoData: PromisesWidgetSummaryApi = {
    promisesDueToday: {
      count: promisesDueToday.length,
    },
    brokenPromises: {
      count: brokenPromises.length,
      totalAmount: brokenPromises.reduce((sum, x) => sum + x.promisedAmount, 0),
    },
  };

  // Build query params — only include assignedUserId when a specific user is selected
  const params: Record<string, string | number | boolean | undefined> | undefined = assignedUserId
    ? { assignedUserId }
    : undefined;

  return useModeAwareQuery<PromisesWidgetSummaryApi, Error>(
    ['promises-widget-summary', assignedUserId ?? 'everyone'],
    async ({ signal }) => {
      return apiGet<PromisesWidgetSummaryApi>('/api/v1/promises/widget-summary', params, signal);
    },
    demoData,
    { staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: false },
  );
}
